import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {ContainableSummary} from 'constants/SampleConstants';
import StorableStore from 'samples/StorableStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_MINI_PADDING} from 'ui-library/chips/ChipConstants';
import ChipList from 'ui-library/chips/ChipList';
import {GRAY_LIGHT} from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {WorklistType} from 'worklists/WorklistConstants';

import {
  COLUMN_TYPE,
  SCANNED_CONTAINERS_TABLE_COLUMNS,
  SCANNED_WORKLIST_ITEMS_TABLE_COLUMNS,
  ScannedWorklistItem,
} from './BarcodeConstants';

interface EnhancedProps {
  containedItems: Array<ContainableSummary>;
}
interface ScannedWorklistItemRowProps {
  scannedWorklistItem: ScannedWorklistItem;
  handleRemoveRow: (scannedWorklistItem: ScannedWorklistItem) => void;
  worklistType: WorklistType;
}

class ScannedWorklistItemRowConnector extends DataConnector<ScannedWorklistItemRowProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [StorableStore];

  enhanceProps(): EnhancedProps {
    let containedItems: Array<ContainableSummary> = [];
    if (this.props.worklistType === WorklistType.CONTAINER && this.props.scannedWorklistItem.apiId) {
      const containedSummaries = this.storableStore().getStorableSummaryExtras().containedItemsByContainerId;
      if (containedSummaries) {
        containedItems = containedSummaries[this.props.scannedWorklistItem.apiId] || [];
      }
    }
    return {containedItems};
  }
}

export function getScannedTableColumns(
  worklistType: WorklistType
): Array<{type: COLUMN_TYPE; width: number}> {
  if (worklistType === WorklistType.CONTAINER) {
    return SCANNED_CONTAINERS_TABLE_COLUMNS;
  } else {
    return SCANNED_WORKLIST_ITEMS_TABLE_COLUMNS;
  }
}

function ContainerContents({ids}: {ids: Array<string>}): JSX.Element {
  return (
    <div>
      <ChipList itemIds={ids} chipSize={ChipSize.MINI} maxChips={2} />
    </div>
  );
}

/**
 * Presentational component to render a barcode and information about its worklist item.
 * Tested in ScanIntoWorklistModal-test.
 */
function ScannedWorklistItemRow({
  scannedWorklistItem,
  handleRemoveRow,
  worklistType,
  containedItems,
}: ScannedWorklistItemRowProps & EnhancedProps): JSX.Element {
  let scannedWorklistItemComponent: JSX.Element;
  if (scannedWorklistItem.apiId) {
    // Override default onClick behavior so clicking on a chip doesn't open the item behind the modal
    scannedWorklistItemComponent = (
      <BenchChip
        id={scannedWorklistItem.apiId}
        size={ChipSize.MINI}
        onClick={() => {}}
        dataTestAttrs={{component: 'ScannedWorklistItemRow', element: 'componentColumn'}}
      />
    );
  } else if (scannedWorklistItem.notFound) {
    scannedWorklistItemComponent = (
      <Button icon="warning" buttonStyle={ButtonStyle.NEGATIVE} size={ButtonSize.MINI} text="Not found" />
    );
  } else {
    scannedWorklistItemComponent = (
      <div
        className={css(styles.throbber)}
        {...getTestProps({component: 'ScannedWorklistItemRow', element: 'componentColumn'})}
      >
        <HorizontalThrobber />
      </div>
    );
  }

  function getColumn(type: COLUMN_TYPE): JSX.Element | string {
    switch (type) {
      case COLUMN_TYPE.SCANNED_IDENTIFIER:
        return scannedWorklistItem.scannedIdentifier;
      case COLUMN_TYPE.CHIP:
        return scannedWorklistItemComponent;
      case COLUMN_TYPE.CONTENTS:
        assert(worklistType === WorklistType.CONTAINER, 'Contents valid only for container type.');
        return <ContainerContents ids={containedItems.map((item) => item.id)} />;
      case COLUMN_TYPE.TRASH:
        return (
          <BenchlingIcon
            className={css(styles.trash)}
            icon="trash-can"
            onClick={() => {
              handleRemoveRow(scannedWorklistItem);
            }}
          />
        );
      default:
        throw new Error('Unexpected column name');
    }
  }
  return (
    <Row
      className={css(styles.tableRow)}
      dataTestAttrs={{component: 'ScannedWorklistItemRow', element: 'root'}}
    >
      {getScannedTableColumns(worklistType).map(({type, width}) => (
        <Col sm={width} key={type}>
          {getColumn(type)}
        </Col>
      ))}
    </Row>
  );
}

export default ScannedWorklistItemRowConnector.connect(ScannedWorklistItemRow);

const styles = StyleSheet.create({
  tableRow: {
    marginBottom: 0,
    padding: '5px 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderTop: `1px solid ${GRAY_LIGHT}`,
  },
  throbber: {
    paddingBottom: `${CHIP_MINI_PADDING}px`,
    paddingTop: `${CHIP_MINI_PADDING}px`,
  },
  trash: {
    cursor: 'pointer',
  },
});
